import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Check, Copy } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { migrationApi, type MigrationProposal, type MigrationSnapshot } from '@/api/migration';
import { licensingApi } from '@/api/licensing';
import { LIMIT_KEYS, LIMIT_LABELS, type LimitKey } from '@/types/licensing';
import type { SystemLicensePlan } from '@/types';
import { formatNIS } from '@/utils/currency';

const STEPS = ['Snapshot', 'Proposal', 'Diff preview', 'Comms', 'Apply'] as const;
const TARGET_SYSTEM = 'jumpstart';

export default function WizardPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  const [snapshot, setSnapshot] = useState<MigrationSnapshot | null>(null);
  const [toPlan, setToPlan] = useState<string>('ignite');
  const [effectivePriceNis, setEffectivePriceNis] = useState<string>('');
  const [overrideReason, setOverrideReason] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  const orgQ = useQuery({
    queryKey: ['org', orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name, email')
        .eq('id', orgId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!orgId,
  });

  const plansQ = useQuery({
    queryKey: ['license-plans', TARGET_SYSTEM],
    queryFn: () => licensingApi.listPlans(TARGET_SYSTEM),
  });

  const suggestionQ = useQuery({
    queryKey: ['migration', 'suggestion', orgId],
    queryFn: () => migrationApi.computeSuggestion(orgId!),
    enabled: !!orgId,
  });

  useEffect(() => {
    if (suggestionQ.data?.suggested_plan && step === 0) {
      setToPlan(suggestionQ.data.suggested_plan);
    }
  }, [suggestionQ.data, step]);

  useEffect(() => {
    if (!orgId || snapshot) return;
    migrationApi.buildSnapshot(orgId).then(setSnapshot).catch((e) => toast.error(String(e.message ?? e)));
  }, [orgId, snapshot]);

  const targetPlan: SystemLicensePlan | undefined = useMemo(
    () => (plansQ.data ?? []).find((p) => p.code === toPlan),
    [plansQ.data, toPlan],
  );

  const usage = snapshot?.usage ?? suggestionQ.data?.usage ?? {};

  const diff = useMemo(() => {
    if (!targetPlan) return [];
    const limits = (targetPlan.limits ?? {}) as Record<string, number>;
    return LIMIT_KEYS.map((k: LimitKey) => {
      const used = Number(usage[k] ?? 0);
      const limit = Number(limits[k] ?? 0);
      const over = limit !== -1 && used > limit;
      return { key: k, used, limit, over };
    });
  }, [targetPlan, usage]);

  const hasOverage = diff.some((d) => d.over);

  const proposal: MigrationProposal = useMemo(
    () => ({
      to_system_code: TARGET_SYSTEM,
      to_plan_code: toPlan,
      addons: [],
      effective_price_agorot: effectivePriceNis.trim()
        ? Math.round(Number(effectivePriceNis) * 100)
        : targetPlan
          ? targetPlan.price_monthly_agorot
          : null,
      override_reason: overrideReason.trim() || null,
      notes: notes.trim() || null,
    }),
    [toPlan, effectivePriceNis, overrideReason, notes, targetPlan],
  );

  const applyMut = useMutation({
    mutationFn: async () => {
      if (!orgId || !snapshot) throw new Error('Snapshot missing');
      return migrationApi.apply({ orgId, proposal, snapshot });
    },
    onSuccess: (id) => {
      toast.success(`Migrated — log ${id.slice(0, 8)}…`);
      navigate(`/organizations/${orgId}`);
    },
    onError: (e: any) => toast.error(e.message ?? 'Apply failed'),
  });

  const commsText = useMemo(() => {
    const from = snapshot?.subscription
      ? `${snapshot.subscription.system_code} / ${snapshot.subscription.plan_code}`
      : '(no active sub)';
    const priceNis = proposal.effective_price_agorot != null ? formatNIS(proposal.effective_price_agorot) : 'TBD';
    return `Hi ${orgQ.data?.name ?? ''},

We're moving your subscription from ${from} to Jumpstart ${toPlan}.

New monthly price: ${priceNis}.
${overrideReason ? `\nNote: ${overrideReason}\n` : ''}
If anything looks off, reply to this email within 7 days.

— Otomator`;
  }, [orgQ.data, snapshot, toPlan, proposal.effective_price_agorot, overrideReason]);

  return (
    <div className="p-6 space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/migration/discovery" className="text-xs text-gray-400 hover:text-white">← Back to discovery</Link>
          <h1 className="text-2xl font-semibold text-white mt-1">
            Migrate {orgQ.data?.name ?? '…'}
          </h1>
        </div>
      </div>

      <div className="flex gap-2">
        {STEPS.map((label, idx) => (
          <button
            key={label}
            onClick={() => setStep(idx)}
            className="flex-1 px-3 py-2 rounded-lg text-xs"
            style={{
              background: step === idx ? '#6366f1' : '#1a1d27',
              color: step === idx ? 'white' : '#9ca3af',
              border: '1px solid #2a2d3e',
            }}
          >
            {idx + 1}. {label}
          </button>
        ))}
      </div>

      <div className="rounded-xl p-5" style={{ background: '#1a1d27', border: '1px solid #2a2d3e' }}>
        {step === 0 && (
          <div className="space-y-3">
            <h2 className="text-white font-medium">Current state</h2>
            {!snapshot ? (
              <div className="text-gray-500 text-sm">Building snapshot…</div>
            ) : (
              <>
                <div className="text-sm text-gray-300">
                  Active subscription:{' '}
                  {snapshot.subscription ? (
                    <span className="text-white">
                      {snapshot.subscription.system_code} / {snapshot.subscription.plan_code}
                    </span>
                  ) : (
                    <span className="text-gray-500">none</span>
                  )}
                </div>
                <div className="text-sm text-gray-300">
                  Active add-ons: <span className="text-white">{snapshot.addons.length}</span>
                </div>
                <pre className="text-xs text-gray-400 p-3 rounded overflow-x-auto" style={{ background: '#0f1117' }}>
{JSON.stringify(snapshot.usage, null, 2)}
                </pre>
              </>
            )}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <h2 className="text-white font-medium">Proposal</h2>
            {suggestionQ.data && (
              <div className="text-xs text-gray-400">
                Suggested based on usage: <span className="text-indigo-300">{suggestionQ.data.suggested_plan}</span>
              </div>
            )}
            <div>
              <label className="block text-xs text-gray-400 mb-1">Target plan</label>
              <select
                value={toPlan}
                onChange={(e) => setToPlan(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm text-white"
                style={{ background: '#0f1117', border: '1px solid #2a2d3e' }}
              >
                {(plansQ.data ?? []).map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.name} — {formatNIS(p.price_monthly_agorot)}/mo
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Effective price override (NIS/mo)</label>
              <input
                type="number"
                value={effectivePriceNis}
                onChange={(e) => setEffectivePriceNis(e.target.value)}
                placeholder={targetPlan ? String(targetPlan.price_monthly_agorot / 100) : ''}
                className="w-full px-3 py-2 rounded-lg text-sm text-white"
                style={{ background: '#0f1117', border: '1px solid #2a2d3e' }}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Override reason</label>
              <input
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm text-white"
                style={{ background: '#0f1117', border: '1px solid #2a2d3e' }}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 rounded-lg text-sm text-white"
                style={{ background: '#0f1117', border: '1px solid #2a2d3e' }}
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <h2 className="text-white font-medium">Usage vs proposed limits</h2>
            {hasOverage && (
              <div className="text-xs text-amber-400">
                ⚠ Current usage exceeds at least one limit on <b>{toPlan}</b>. Apply is blocked unless you pick a larger plan or add an add-on.
              </div>
            )}
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 text-xs">
                  <th className="py-1">Key</th>
                  <th className="py-1">Used</th>
                  <th className="py-1">New limit</th>
                  <th className="py-1"></th>
                </tr>
              </thead>
              <tbody>
                {diff.map((d) => (
                  <tr key={d.key} style={{ borderTop: '1px solid #2a2d3e' }}>
                    <td className="py-1.5 text-gray-300">{LIMIT_LABELS[d.key]?.label ?? d.key}</td>
                    <td className="py-1.5 text-white">{d.used}</td>
                    <td className="py-1.5 text-white">{d.limit === -1 ? 'Unlimited' : d.limit}</td>
                    <td className="py-1.5 text-right">
                      {d.over && <span className="text-amber-400 text-xs">over</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            <h2 className="text-white font-medium">Customer comms</h2>
            <textarea
              value={commsText}
              readOnly
              rows={10}
              className="w-full px-3 py-2 rounded-lg text-sm text-gray-200 font-mono"
              style={{ background: '#0f1117', border: '1px solid #2a2d3e' }}
            />
            <button
              onClick={() => {
                navigator.clipboard.writeText(commsText);
                toast.success('Copied to clipboard');
              }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs"
              style={{ background: '#6366f1', color: 'white' }}
            >
              <Copy size={14} /> Copy
            </button>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <h2 className="text-white font-medium">Apply migration</h2>
            <pre className="text-xs text-gray-400 p-3 rounded overflow-x-auto" style={{ background: '#0f1117' }}>
{JSON.stringify(proposal, null, 2)}
            </pre>
            {hasOverage && (
              <div className="text-xs text-amber-400">Cannot apply: usage exceeds proposed limits.</div>
            )}
            <button
              disabled={!snapshot || hasOverage || applyMut.isPending}
              onClick={() => applyMut.mutate()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm disabled:opacity-50"
              style={{ background: '#6366f1', color: 'white' }}
            >
              <Check size={16} /> {applyMut.isPending ? 'Applying…' : 'Apply migration'}
            </button>
          </div>
        )}
      </div>

      <div className="flex justify-between">
        <button
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
          className="px-4 py-2 rounded-lg text-sm disabled:opacity-40"
          style={{ background: '#1a1d27', color: '#9ca3af', border: '1px solid #2a2d3e' }}
        >
          ← Previous
        </button>
        <button
          onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
          disabled={step === STEPS.length - 1}
          className="px-4 py-2 rounded-lg text-sm disabled:opacity-40"
          style={{ background: '#1a1d27', color: '#9ca3af', border: '1px solid #2a2d3e' }}
        >
          Next →
        </button>
      </div>
    </div>
  );
}
